// The findings behind the posture screen (#639, D14): every account's latest
// scan, failing findings first by default, a page at a time.
//
// Two findings can share a finding id (two inline policies on one role); each is
// its own row here, as the store keeps them (D13), keyed by its place in its scan.

import { useEffect, useRef, useState } from "react";

import { ApiError, browserApi, messageOf, type Api } from "../api/http";
import {
  readFindings,
  type Finding,
  type FindingsFilter,
  type FindingsPage as Page,
  type ScanRecord,
} from "../api/posture";
import { AccountName } from "./AccountName";
import { Chip, Empty, Field, Notice, SelectField } from "./chrome";
import { FindingFixAdvice } from "./FindingFixAdvice";
import { readableTime } from "./runPresentation";
import { SEVERITY_LEVELS, Severity, severityWord } from "./Severity";

export interface FindingsActions {
  readFindings(filter: FindingsFilter): Promise<Page>;
}

/** A refused read says what the person cannot do, not which permission string it lacked. */
export function findingsMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) {
    return "You don't have permission to view findings.";
  }
  return messageOf(error);
}

const STATUSES: ReadonlyArray<[NonNullable<FindingsFilter["status"]>, string]> = [
  ["FAIL", "Failing"],
  ["PASS", "Passing"],
  ["MANUAL", "Manual"],
];
/** What the page reads first, and what the console reads on arrival. */
export const DEFAULT_FINDINGS_FILTER: FindingsFilter = { status: "FAIL" };

/** A finished scan as the scan filter names it: the account, then when it finished. */
const scanChoice = (scan: ScanRecord): string =>
  `${scan.account_name ?? scan.account_id} · ${readableTime(scan.completed_at ?? scan.started_at)}`;

/** How many findings the filters match, and how many of them are on the screen. */
const countText = (shown: number, total: number): string => {
  const findings = total === 1 ? "1 finding" : `${total} findings`;
  return shown < total ? `Showing ${shown} of ${findings}` : findings;
};

const tone = (f: Finding) => (f.status === "FAIL" ? "bad" : f.status === "PASS" ? "good" : "warn");

export function FindingsPage({
  first,
  error,
  actions,
  scans = [],
  reloadKey,
}: {
  first: Page | null;
  error: string | null;
  actions: FindingsActions;
  /** The workspace's scans; the finished ones are offered as the scan filter's choices. */
  scans?: ScanRecord[];
  reloadKey?: number;
}) {
  const [filter, setFilter] = useState<FindingsFilter>(DEFAULT_FINDINGS_FILTER);
  const [service, setService] = useState("");
  const [pages, setPages] = useState<Page[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const shown = pages ?? (first === null ? [] : [first]);
  const items = shown.flatMap((p) => p.items);
  const last = shown.length === 0 ? null : shown[shown.length - 1];
  const next = last === null ? null : last.next_cursor;

  const readVersion = useRef(0);
  useEffect(() => () => { readVersion.current += 1; }, []);

  const read = (wanted: FindingsFilter, append: boolean) => {
    const version = ++readVersion.current;
    setFailure(null);
    actions.readFindings(wanted).then(
      (page) => {
        if (readVersion.current === version) setPages(append ? [...shown, page] : [page]);
      },
      (e: unknown) => {
        if (readVersion.current === version) setFailure(findingsMessage(e));
      },
    );
  };
  const lastReload = useRef(reloadKey);
  useEffect(() => {
    if (lastReload.current === reloadKey) return;
    lastReload.current = reloadKey;
    read({ ...filter, cursor: null }, false);
  }, [reloadKey]);

  const choose = (change: Partial<FindingsFilter>) => {
    // What the Service field shows is always what is asked, sent or only typed.
    const wanted = { ...filter, service: service.trim() || undefined, ...change, cursor: null };
    setFilter(wanted);
    read(wanted, false);
  };

  return (
    <>
      {error === null || pages !== null ? null : <Notice tone="bad">{error}</Notice>}
      {failure === null ? null : <Notice tone="bad">{failure}</Notice>}
      <section className="panel">
        <div className="panel__head">
          <h2>Findings</h2>
          {last === null ? null : <Chip>{countText(items.length, last.total)}</Chip>}
        </div>
        <form
          className="findings-filters"
          aria-label="Filter the findings"
          onSubmit={(e) => {
            e.preventDefault();
            choose({ service: service.trim() || undefined });
          }}
        >
          <SelectField
            label="Status"
            id="findings-status"
            value={filter.status ?? ""}
            onChange={(e) =>
              choose({
                status: (e.target.value || undefined) as FindingsFilter["status"],
              })
            }
          >
            <option value="">Any status</option>
            {STATUSES.map(([value, word]) => (
              <option key={value} value={value}>
                {word}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Severity"
            id="findings-severity"
            value={filter.severity ?? ""}
            onChange={(e) => choose({ severity: e.target.value || undefined })}
          >
            <option value="">Any severity</option>
            {SEVERITY_LEVELS.map((level) => (
              <option key={level} value={level}>
                {severityWord(level)}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Scan"
            id="findings-scan"
            value={filter.scan_id ?? ""}
            onChange={(e) => choose({ scan_id: e.target.value || undefined })}
          >
            <option value="">Every account&apos;s latest scan</option>
            {scans
              .filter((scan) => scan.status === "done" && scan.findings_held)
              .map((scan) => (
                <option key={scan.scan_id} value={scan.scan_id}>
                  {scanChoice(scan)}
                </option>
              ))}
          </SelectField>
          <Field
            label="Service"
            id="findings-service"
            placeholder="s3, iam, …"
            value={service}
            onChange={(e) => setService(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <p className="findings-filters__apply">
            <button type="submit" className="btn">
              Filter
            </button>
          </p>
        </form>
        {first === null && pages === null && error === null ? (
          <p className="muted panel__body" role="status">
            Reading the findings…
          </p>
        ) : items.length === 0 ? (
          <Empty title="No finding matches." />
        ) : (
          <div className="table-wrap" tabIndex={0}>
            <table className="table findings-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Severity</th>
                  <th>Check</th>
                  <th>Resource</th>
                  <th>Account · region</th>
                </tr>
              </thead>
              <tbody>
                {items.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <Chip tone={tone(f)}>{f.status}</Chip>
                    </td>
                    <td>
                      <Severity level={f.severity} />
                    </td>
                    <td>
                      {f.check_title || f.check_id}
                      <div className="muted">{f.status_extended}</div>
                      {f.status === "FAIL" ? <FindingFixAdvice finding={f} /> : null}
                    </td>
                    <td>{f.resource_name || f.resource_uid}</td>
                    <td>
                      <AccountName id={f.account_id} name={f.account_name} /> · {f.region}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {next === null ? null : (
          <div className="actions panel__body">
            <button
              type="button"
              className="btn"
              onClick={() => read({ ...filter, cursor: next }, true)}
            >
              More findings
            </button>
          </div>
        )}
      </section>
    </>
  );
}

/** The screen as the console mounts it: the first page of failing findings on arrival. */
export function FindingsScreen({
  api = browserApi,
  scans = [],
  reloadKey,
}: {
  api?: Api;
  scans?: ScanRecord[];
  reloadKey?: number;
}) {
  const [first, setFirst] = useState<Page | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void readFindings(api, DEFAULT_FINDINGS_FILTER).then(
      (page) => live && setFirst(page),
      (reason: unknown) => live && setError(findingsMessage(reason)),
    );
    return () => {
      live = false;
    };
  }, [api]);
  return (
    <FindingsPage
      first={first}
      error={error}
      actions={{ readFindings: (filter) => readFindings(api, filter) }}
      scans={scans}
      reloadKey={reloadKey}
    />
  );
}
